import yup from "src/validators/yup.Module";
import { PessoaDto } from "../../Entidade/Pessoa/Pessoa.Dto";

export enum EStatusEnvioCte {
  AguardandoEnvio = 0,
  EnviadaSemRetorno = 1,
  Finalizada = 2,
  FinalizadaDenegada = 3,
  FinalizadaCancelada = 4,
}

export interface CTeFiltrarDto {
  codigoStatusEnvio?: string | number | null;
  codigosStatusEnvio?: (string | number | EStatusEnvioCte)[] | null;
  dataEmissaoInicial?: Date | null;
  dataEmissaoFinal?: Date | null;
  serie?: number | null;
  id?: number | null;
  chaveAcesso?: string | null;
  codigoMunicipioDestinatario?: number | null;
  codigoMunicipioRemetente?: number | null;
  codigosMunicipioRemetente?: number[] | null;
}

export const CTeFiltrarSchema = yup.object().shape({
  nome: yup
    .string()
    .notRequired()
    .min(3, "O nome deve ter no mínimo 3 caracteres"),
});

export interface CTePessoaDto extends Omit<Partial<PessoaDto>, "codigoMunicipio"> {
  documento: string;
  inscricaoEstadual?: string;
  nome: string;
  nomeFantasia?: string;
  indicadorIETomador?: number;
  fone?: string;
  endereco: string;
  enderecoNumero: string;
  bairro: string;
  codigoMunicipio: string | number;
  nomeMunicipio: string;
  uf: string;
  cep: string;
  codigoPais?: string;
  nomePais?: string;
}

export interface CTeValoresComponenteDto {
  nome: string;
  valor: number;
}

export interface CTeValoresPrestacaoDto {
  valorPrestacaoServico: number;
  valorReceber: number;
  valorTotalTributos: number;
  componentes: CTeValoresComponenteDto[];
}

export interface CTeCargaQuantidadeDto {
  codigoUnidadeMedida: string;
  tipoMedida: string;
  quantidadeCarga: number;
}

export interface CTeCargaDto {
  valorCarga: number;
  produtoPredominante: string;
  outraCaracteristica?: string;
  quantidades: CTeCargaQuantidadeDto[];
}

export interface CTeComplementoDto {
  observacao?: string;
}

export interface CTeDocumentoDto {
  tipoDocumento: number;
  chaveDocumento: string;
}

export interface CTeAutorizacaoDto {
  codigoStatusEnvio: number;
  descricaoStatusEnvio: string;
  codigoStatusResposta: number;
  descricaoMotivoResposta: string;
  chaveAcesso?: string;
  numeroProtocolo?: string;
}

export interface CTeICMSDto {
  id?: number;
  cst: number;
  valorBaseCalculo: number;
  percentualAliquota: number;
  valorImposto: number;
  percentualReducaoBaseCalculo?: number;
}

export interface CTeIBSCBSDto {
  id?: number;
  cst?: number | string;
  classTrib?: string;
  cClassifTrib?: string;
  valorBC?: number;
  percentualUFIBS?: number;
  percentualUFAliquotaEfetivaIBS?: number;
  valorUFIbs?: number;
  percentualMunicipioIBS?: number;
  percentualMunicipioAliquotaEfetivaIBS?: number;
  valorMunicipioIBS?: number;
  valorIBS?: number;
  percentualCBS?: number;
  percentualAliquotaEfetivaCBS?: number;
  valorCBS?: number;
}

export const cstIcmsCteDescricao: Record<number, string> = {
  0: "00 - Tributação normal ICMS",
  20: "20 - BC reduzida ICMS",
  40: "40 - Isenta",
  41: "41 - Não tributada",
  51: "51 - Diferida",
  60: "60 - ICMS cobrado anteriormente por ST",
  90: "90 - Outros",
  101: "101 - Simples Nacional com permissão de crédito",
  102: "102 - Simples Nacional sem permissão de crédito",
  103: "103 - Simples Nacional isenção",
  201: "201 - Simples Nacional com crédito e ST",
  202: "202 - Simples Nacional sem crédito e ST",
  203: "203 - Simples Nacional isenção e ST",
  300: "300 - Imune",
  400: "400 - Não tributada Simples Nacional",
  500: "500 - ICMS cobrado anteriormente por ST",
  900: "900 - Outros (Simples Nacional)",
};

export interface CTeDto {
  id?: number;
  serie: number;
  numeroCte: number;
  chaveAcesso?: string;
  tributacaoId?: number;
  tributacaoNome?: string;
  cfop?: string;
  naturezaOperacao?: string;
  tipoCte: number;
  chaveCTeComplementado?: string | null;
  tipoServico: number;
  finalidadeCte: number;
  modal: number;
  retira: number;
  tipoTomador: number;
  rntrc?: string;
  codigoMunicipioInicio: number;
  nomeMunicipioInicio: string;
  ufInicio: string;
  codigoMunicipioFim: number;
  nomeMunicipioFim: string;
  ufFim: string;
  destinatario: CTePessoaDto;
  remetente: CTePessoaDto;
  tomador?: CTePessoaDto;
  valoresPrestacao: CTeValoresPrestacaoDto;
  carga?: CTeCargaDto;
  complemento?: CTeComplementoDto;
  documentos?: CTeDocumentoDto[];
  emissaoDataHora?: string;
  autorizacao?: CTeAutorizacaoDto;
  icms?: CTeICMSDto;
  ibscbs?: CTeIBSCBSDto;
}

export const TIPO_CTE_COMPLEMENTO_VALORES = 1;
export const FINALIDADE_CTE_COMPLEMENTAR = 1;

export const isCteComplementoValores = (tipoCte?: number | string | null) =>
  Number(tipoCte) === TIPO_CTE_COMPLEMENTO_VALORES;

/** Valida chave de acesso de CT-e (44 dígitos, modelo 57, DV módulo 11). */
export const validarChaveCTe = (chave?: string | null): string | null => {
  const valor = (chave ?? "").trim();
  if (!valor) return "Chave do CT-e complementado é obrigatória";
  if (!/^\d+$/.test(valor)) return "Chave contém caracteres inválidos";
  if (valor.length !== 44) return "A chave deve conter exatamente 44 dígitos";
  if (valor.substring(20, 22) !== "57")
    return "A chave deve ser de um CT-e (modelo 57)";
  let peso = 2;
  let soma = 0;
  for (let i = 42; i >= 0; i--) {
    soma += Number(valor[i]) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  const resto = soma % 11;
  const dv = resto < 2 ? 0 : 11 - resto;
  if (dv !== Number(valor[43])) return "Chave com dígito verificador inválido";
  return null;
};

/** Remove do payload os dados que a API não aceita no CT-e de complemento de valores. */
export const montarPayloadCTe = (cte: CTeDto): CTeDto => {
  if (!isCteComplementoValores(cte.tipoCte)) {
    return { ...cte, chaveCTeComplementado: undefined };
  }
  const { carga, rntrc, ...resto } = cte;
  return { ...resto, documentos: [] };
};

export const tipoCteOptions = [
  { value: 0, label: "0 - Normal" },
  { value: 1, label: "1 - Complemento de Valores" },
  { value: 2, label: "2 - Anulação" },
  { value: 3, label: "3 - Substituto" },
];

export const tipoServicoOptions = [
  { value: 0, label: "0 - Normal" },
  { value: 1, label: "1 - Subcontratação" },
  { value: 2, label: "2 - Redespacho" },
  { value: 3, label: "3 - Redespacho Intermediário" },
  { value: 4, label: "4 - Serviço Vinculado a Multimodal" },
];

export const finalidadeCteOptions = [
  { value: 0, label: "0 - Normal" },
  { value: 1, label: "1 - Complementar" },
];

export const modalOptions = [
  { value: 1, label: "01 - Rodoviário" },
  { value: 2, label: "02 - Aéreo" },
  { value: 3, label: "03 - Aquaviário" },
  { value: 4, label: "04 - Ferroviário" },
  { value: 5, label: "05 - Dutoviário" },
  { value: 6, label: "06 - Multimodal" },
];

export const retiraOptions = [
  { value: 0, label: "0 - Sim" },
  { value: 1, label: "1 - Não" },
];

export const tipoTomadorOptions = [
  { value: 0, label: "0 - Remetente" },
  { value: 1, label: "1 - Expedidor" },
  { value: 2, label: "2 - Recebedor" },
  { value: 3, label: "3 - Destinatário" },
  { value: 4, label: "4 - Outros" },
];

export const tipoDocumentoOptions = [
  { value: 1, label: "1 - NF-e (Chave de Acesso)" },
  { value: 2, label: "2 - Declaração" },
  { value: 3, label: "3 - Outros" },
];

export const unidadeMedidaOptions = [
  { value: "00", label: "00 - M3" },
  { value: "01", label: "01 - KG" },
  { value: "02", label: "02 - TON" },
  { value: "03", label: "03 - UNIDADE" },
  { value: "04", label: "04 - LITROS" },
  { value: "05", label: "05 - MMBTU" },
];

export const STORAGECTE_KEY = "cteTemp";

export function updateLocal<T extends object>(
  key: string,
  partial: Partial<T> | null
) {
  if (partial === null) {
    localStorage.removeItem(key);
    return;
  }

  const stored = localStorage.getItem(key);
  const current: T = stored ? JSON.parse(stored) : ({} as T);

  const updated: T = {
    ...current,
    ...partial,
  };

  localStorage.setItem(key, JSON.stringify(updated));
}

export function getLocal<T>(key: string): T | null {
  const stored = localStorage.getItem(key);
  return stored ? (JSON.parse(stored) as T) : null;
}

export interface VisualStatusConfigCTe {
  categoria:
    | "nao_enviado"
    | "enviado_autorizado"
    | "cancelado"
    | "rejeitado"
    | "em_processamento";
  label: string;
  badgeBg: string;
  badgeText?: string;
  borderClass: string;
  iconSymbol: string;
}

export const statusEnvioCteOptions = [
  { value: "99", label: "Todos os Status" },
  { value: "0", label: "0 - Aguardando Envio" },
  { value: "1", label: "1 - Enviada Sem Retorno" },
  { value: "2", label: "2 - Finalizada" },
  { value: "3", label: "3 - Finalizada Denegada" },
  { value: "4", label: "4 - Finalizada Cancelada" },
  { value: "3,4", label: "Cancelados / Denegados" },
];

export const statusEnvioCteDescricao: Record<number, string> = {
  [EStatusEnvioCte.AguardandoEnvio]: "Aguardando Envio",
  [EStatusEnvioCte.EnviadaSemRetorno]: "Enviada Sem Retorno",
  [EStatusEnvioCte.Finalizada]: "Finalizada",
  [EStatusEnvioCte.FinalizadaDenegada]: "Finalizada Denegada",
  [EStatusEnvioCte.FinalizadaCancelada]: "Finalizada Cancelada",
};

export const getVisualStatusCTe = (item: CTeDto): VisualStatusConfigCTe => {
  const aut = item.autorizacao;

  // 1. Se não possui objeto de autorização ou não tem status
  if (!aut || aut.codigoStatusEnvio === undefined || aut.codigoStatusEnvio === null) {
    return {
      categoria: "nao_enviado",
      label: "Não Enviado",
      badgeBg: "warning",
      badgeText: "dark",
      borderClass: "border-warning",
      iconSymbol: "⏳",
    };
  }

  const codEnvio = Number(aut.codigoStatusEnvio);
  const codResp = Number(aut.codigoStatusResposta);
  const descEnvio = (aut.descricaoStatusEnvio || "").toLowerCase();
  const descResp = (aut.descricaoMotivoResposta || "").toLowerCase();

  // 2. Cancelado (Status 4 - Finalizada Cancelada ou retorno de cancelamento)
  if (
    codEnvio === EStatusEnvioCte.FinalizadaCancelada ||
    codEnvio === 4 ||
    codResp === 101 ||
    codResp === 135 ||
    codResp === 136 ||
    descEnvio.includes("cancelad") ||
    descResp.includes("cancelad")
  ) {
    return {
      categoria: "cancelado",
      label: "Cancelado",
      badgeBg: "danger",
      badgeText: "white",
      borderClass: "border-danger",
      iconSymbol: "✕",
    };
  }

  // 3. Denegado (Status 3 - Finalizada Denegada ou retorno de denegação)
  if (
    codEnvio === EStatusEnvioCte.FinalizadaDenegada ||
    codEnvio === 3 ||
    [110, 301, 302, 303, 304, 305].includes(codResp) ||
    descEnvio.includes("denegad") ||
    descResp.includes("denegad")
  ) {
    return {
      categoria: "rejeitado",
      label: "Denegado",
      badgeBg: "danger",
      badgeText: "white",
      borderClass: "border-danger",
      iconSymbol: "⚠",
    };
  }

  // 4. Autorizado / Finalizado
  if (codResp === 100 || codEnvio === 2) {
    return {
      categoria: "enviado_autorizado",
      label: "Enviado e Autorizado",
      badgeBg: "success",
      badgeText: "white",
      borderClass: "border-success",
      iconSymbol: "✓",
    };
  }

  // 5. Rejeitado
  if (codResp > 100) {
    return {
      categoria: "rejeitado",
      label: "Rejeitado pela SEFAZ",
      badgeBg: "danger",
      badgeText: "white",
      borderClass: "border-danger",
      iconSymbol: "⚠",
    };
  }

  // 6. Enviada sem retorno / Em transmissão
  if (codEnvio === 1) {
    return {
      categoria: "em_processamento",
      label: "Em Transmissão",
      badgeBg: "info",
      badgeText: "white",
      borderClass: "border-info",
      iconSymbol: "🔄",
    };
  }

  // 7. Aguardando Envio / Pendente (0 ou padrão)
  return {
    categoria: "nao_enviado",
    label: "Não Enviado",
    badgeBg: "warning",
    badgeText: "dark",
    borderClass: "border-warning",
    iconSymbol: "⏳",
  };
};

